/**
 * ETL File Service - API client for the ETL file server (http://localhost:8600)
 *
 * Handles CRUD operations for connection profiles and pipelines,
 * plus helper functions for splitting config fields between
 * connection-scoped and pipeline-scoped sections.
 */

import { ETL_NODE_REGISTRY } from './etlNodeRegistry';

const BASE_URL = 'http://localhost:8600';

// ---------------------------------------------------------------------------
// Sections whose fields belong to a connection profile (not pipeline config)
// ---------------------------------------------------------------------------
export const CONNECTION_SECTIONS = ['Connection', 'Auth', 'AWS Auth', 'Security'];

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

async function request(path, options = {}) {
  const url = `${BASE_URL}${path}`;
  try {
    const res = await fetch(url, {
      headers: { 'Content-Type': 'application/json', ...options.headers },
      ...options,
    });
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      throw new Error(
        `ETL file server responded ${res.status} ${res.statusText} for ${options.method || 'GET'} ${path}${body ? `: ${body}` : ''}`
      );
    }
    // DELETE may return 204 with no body
    if (res.status === 204) return null;
    return res.json();
  } catch (err) {
    if (err.message.startsWith('ETL file server responded')) throw err;
    throw new Error(`ETL file server request failed (${options.method || 'GET'} ${path}): ${err.message}`);
  }
}

/**
 * Extract the connector suffix from a nodeType.
 * "etl-source-sqlserver" -> "sqlserver"
 * "etl-target-s3"        -> "s3"
 */
function connectorSuffix(nodeType) {
  return (nodeType || '')
    .replace(/^etl-source-/, '')
    .replace(/^etl-target-/, '');
}

// ---------------------------------------------------------------------------
// Connections
// ---------------------------------------------------------------------------

export async function listConnections() {
  return request('/api/connections');
}

export async function getConnection(id) {
  return request(`/api/connections/${encodeURIComponent(id)}`);
}

export async function saveConnection(profile) {
  return request('/api/connections', {
    method: 'POST',
    body: JSON.stringify({
      name: profile.name,
      description: profile.description,
      driverId: profile.driverId,
      driverVersionId: profile.driverVersionId,
      nodeType: profile.nodeType,
      category: profile.category,
      connectionFields: profile.connectionFields,
    }),
  });
}

export async function updateConnection(id, profile) {
  return request(`/api/connections/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(profile),
  });
}

export async function deleteConnection(id) {
  return request(`/api/connections/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

/**
 * List connections that are compatible with the given nodeType.
 *
 * A profile is compatible when its connector suffix matches.  For example,
 * both "etl-source-sqlserver" and "etl-target-sqlserver" share the suffix
 * "sqlserver", so a profile saved from either side is visible to both --
 * they use the same host/port/credentials.
 */
export async function listConnectionsByNodeType(nodeType) {
  const all = await listConnections();
  const suffix = connectorSuffix(nodeType);
  return all.filter((p) => connectorSuffix(p.nodeType) === suffix);
}

// ---------------------------------------------------------------------------
// Pipelines
// ---------------------------------------------------------------------------

export async function listPipelines() {
  return request('/api/pipelines');
}

export async function getPipeline(id) {
  return request(`/api/pipelines/${encodeURIComponent(id)}`);
}

export async function savePipeline(pipeline) {
  return request('/api/pipelines', {
    method: 'POST',
    body: JSON.stringify({
      name: pipeline.name,
      description: pipeline.description,
      nodes: pipeline.nodes,
      edges: pipeline.edges,
    }),
  });
}

export async function updatePipeline(id, pipeline) {
  return request(`/api/pipelines/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(pipeline),
  });
}

export async function deletePipeline(id) {
  return request(`/api/pipelines/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

// ---------------------------------------------------------------------------
// Connection field helpers
// ---------------------------------------------------------------------------

/**
 * Return the configSchema fields that belong to connection sections
 * for the given nodeType.
 */
export function getConnectionFields(nodeType) {
  const entry = ETL_NODE_REGISTRY[nodeType];
  if (!entry || !entry.configSchema) return [];
  return entry.configSchema.filter((f) => CONNECTION_SECTIONS.includes(f.section));
}

/**
 * Return the configSchema fields that are NOT connection fields --
 * i.e., pipeline-specific settings (Source, Format, Performance, etc.).
 */
export function getPipelineFields(nodeType) {
  const entry = ETL_NODE_REGISTRY[nodeType];
  if (!entry || !entry.configSchema) return [];
  return entry.configSchema.filter((f) => !CONNECTION_SECTIONS.includes(f.section));
}

/**
 * Given a full node config object, extract only the key/value pairs
 * whose keys correspond to connection fields for the given nodeType.
 */
export function extractConnectionFieldValues(nodeType, config) {
  const connectionKeys = new Set(getConnectionFields(nodeType).map((f) => f.key));
  const result = {};
  for (const key of Object.keys(config || {})) {
    if (connectionKeys.has(key)) {
      result[key] = config[key];
    }
  }
  return result;
}

/**
 * Merge a connection profile's saved fields into the current node config.
 * Connection fields from the profile overwrite; pipeline-specific fields
 * from currentConfig are preserved unchanged.
 */
export function applyConnectionProfile(nodeType, currentConfig, profileConnectionFields) {
  const connectionKeys = new Set(getConnectionFields(nodeType).map((f) => f.key));
  const merged = { ...currentConfig };
  // Overwrite connection keys with profile values
  for (const key of Object.keys(profileConnectionFields || {})) {
    if (connectionKeys.has(key)) {
      merged[key] = profileConnectionFields[key];
    }
  }
  return merged;
}

// ---------------------------------------------------------------------------
// SQL Execution
// ---------------------------------------------------------------------------

/**
 * Test a database connection via the ETL server.
 * @param {{ driver, host, port, database, username, password, schema }} config
 * @returns {Promise<{ success, message?, error?, durationMs }>}
 */
export async function testConnection(config) {
  return request('/api/execute/test-connection', {
    method: 'POST',
    body: JSON.stringify(config),
  });
}

/**
 * Execute SQL statements against a database via the ETL server.
 * @param {{ driver, host, port, database, username, password, schema,
 *           sql?, statements?, delimiter?, onError?, useTransaction? }} payload
 * @returns {Promise<{ success, results[], totalStatements, succeeded, failed, skipped, durationMs }>}
 */
export async function executeSql(payload) {
  return request('/api/execute/sql', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}
